"use client";

import React from "react";
import Link from "next/link";
import { SpinnerIcon } from "./Icons";

type ArrowDir = "right" | "down" | "left" | "up";

type CommonProps = {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  bgStyle?: string;
  arrowStyle?: string;
  loading?: boolean;
  center?: boolean;
  disabled?: boolean;
};

type ButtonAsButton = CommonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: "button";
  };

type ButtonAsAnchor = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children" | "className"> & {
    as: "a";
    href: string;
  };

type ButtonAsNextLink = CommonProps &
  Omit<React.ComponentProps<typeof Link>, "href" | "children" | "className"> & {
    as: "link";
    href: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsNextLink;

function hasDirectionProp(
  node: React.ReactNode
): node is React.ReactElement<{ direction?: ArrowDir }> {
  return React.isValidElement(node) && "direction" in (node.props as object);
}

export const Button: React.FC<ButtonProps> = ({
  children,
  icon,
  className = "",
  bgStyle = "",
  arrowStyle = "",
  loading = false,
  center = false,
  disabled = false,
  ...props
}) => {
  const isDisabled = disabled || loading;

  const arrowRotateClass = className.includes("arrow-up")
    ? "-rotate-90"
    : className.includes("arrow-down")
      ? "rotate-90"
      : "";

  const DefaultArrowIcon = (
    <svg
      className="stroke-current"
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="13"
      viewBox="0 0 18 13"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M1 6.5H17M17 6.5L11.4998 1M17 6.5L11.4998 12"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const defaultHover = bgStyle
    ? ""
    : "bg-white hover:bg-fuji-green border-fuji-black hover:border-fuji-green";

  const layoutClasses = center
    ? "inline-flex items-center justify-center"
    : "inline-flex items-center justify-between";

  const baseClasses = [
    "group",
    layoutClasses,
    "gap-3",
    "h-[42px]",
    "px-5",
    "whitespace-nowrap",
    "rounded-full",
    "border",
    "font-medium",
    "text-fuji-black",
    "transition-colors duration-200",
    "no-underline",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuji-black focus-visible:ring-offset-2",
    isDisabled ? "opacity-50 pointer-events-none" : "",
    defaultHover,
    bgStyle,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const labelClasses = "flex items-center leading-none text-body-1";
  const defaultArrowColor = arrowStyle ? "" : "text-fuji-red";

  const arrowWrapClasses = [
    "flex items-center",
    defaultArrowColor,
    arrowStyle,
    arrowRotateClass,
  ]
    .filter(Boolean)
    .join(" ");

  const hasLabel = !(
    children == null ||
    (typeof children === "string" && children.trim() === "")
  );

  const renderedIcon = loading ? <SpinnerIcon /> : icon ?? DefaultArrowIcon;

  let direction: ArrowDir = "right";

  if (hasDirectionProp(icon) && icon.props.direction) {
    direction = icon.props.direction;
  } else if (className.includes("arrow-down")) {
    direction = "down";
  } else if (className.includes("arrow-up")) {
    direction = "up";
  } else if (className.includes("arrow-left")) {
    direction = "left";
  }

  const hoverTranslate =
    direction === "down"
      ? "group-hover:translate-y-1"
      : direction === "up"
        ? "group-hover:-translate-y-1"
        : direction === "left"
          ? "group-hover:-translate-x-1"
          : "group-hover:translate-x-1";

  const loadingTranslate =
    direction === "down"
      ? "translate-y-1"
      : direction === "up"
        ? "-translate-y-1"
        : direction === "left"
          ? "-translate-x-1"
          : "translate-x-1";

  const iconSlotClasses = [
    "inline-flex h-[19px] w-[18px] items-center justify-center",
    "[&>svg]:h-[19px] [&>svg]:w-[18px]",
    "transform-gpu will-change-transform",
    "transition-transform duration-300",
    "[transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]",
    hoverTranslate,
    loading ? loadingTranslate : "",
  ]
    .filter(Boolean)
    .join(" ");

  const inner = (
    <>
      {hasLabel && <span className={labelClasses}>{children}</span>}
      <span className={arrowWrapClasses}>
        <span className={iconSlotClasses}>{renderedIcon}</span>
      </span>
    </>
  );

  if ((props as ButtonAsNextLink).as === "link") {
    const {
      href,
      prefetch,
      replace,
      scroll,
      shallow,
      locale,
      target,
      rel,
      onClick,
      onMouseEnter,
      onTouchStart,
      id,
      title,
      role,
      tabIndex,
      "aria-label": ariaLabel,
      "aria-current": ariaCurrent,
    } = props as ButtonAsNextLink;

    if (isDisabled) {
      return (
        <span
          className={baseClasses}
          aria-label={ariaLabel}
          aria-current={ariaCurrent}
          aria-disabled={true}
          role={role ?? "link"}
          id={id}
          title={title}
          tabIndex={-1}
        >
          {inner}
        </span>
      );
    }

    return (
      <Link
        href={href}
        prefetch={prefetch}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        locale={locale}
        target={target}
        rel={rel}
        className={baseClasses}
        aria-label={ariaLabel}
        aria-current={ariaCurrent}
        id={id}
        title={title}
        role={role}
        tabIndex={tabIndex}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
      >
        {inner}
      </Link>
    );
  }

  if ((props as ButtonAsAnchor).as === "a") {
    const {
      href,
      target,
      rel,
      onClick,
      id,
      title,
      role,
      tabIndex,
      "aria-label": ariaLabel,
    } = props as ButtonAsAnchor;

    if (isDisabled) {
      return (
        <span
          className={baseClasses}
          aria-label={ariaLabel}
          aria-disabled={true}
          role={role ?? "link"}
          id={id}
          title={title}
          tabIndex={-1}
        >
          {inner}
        </span>
      );
    }

    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={baseClasses}
        aria-label={ariaLabel}
        id={id}
        title={title}
        role={role}
        tabIndex={tabIndex}
        onClick={onClick}
      >
        {inner}
      </a>
    );
  }

  const buttonProps = props as ButtonAsButton;

  return (
    <button
      className={baseClasses}
      {...buttonProps}
      disabled={isDisabled || Boolean(buttonProps.disabled)}
      aria-busy={loading || undefined}
    >
      {inner}
    </button>
  );
};